import { Component, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { SharedModule } from '@/app/shared/shared.module';
import { SatelliteAnnualNdmiAnalysisData, SatelliteViewModel } from '@/app/proxy/proxies';
import { EChartsOption } from 'echarts';
import { TranslateService } from '@ngx-translate/core';
import { PlantationService } from '@/app/core/services/plantation.service';
import { NoAnalysisComponent } from '../no-analysis/no-analysis.component';
import { LuxonFormatPipe } from '@/app/shared/pipes/luxon-format.pipe';
import { DateTime } from 'luxon';

@Component({
  selector: 'app-plant-water-stress',
  imports: [SharedModule, NoAnalysisComponent],
  templateUrl: './plant-water-stress.component.html',
  styleUrl: './plant-water-stress.component.less',
  providers: [LuxonFormatPipe]
})
export class PlantWaterStressComponent implements OnInit, OnChanges {
  private luxonFormatPipe = inject(LuxonFormatPipe);

  @Input() loading = true;
  @Input() data: SatelliteAnnualNdmiAnalysisData[] = [];
  @Input() selectedDay: SatelliteViewModel;
  lastValue: SatelliteAnnualNdmiAnalysisData;

  chartOption!: EChartsOption;

  constructor(private translateService: TranslateService, public plantationService: PlantationService) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['data']) {
      this.makeChart();
    }
  }

  ngOnInit(): void { }

  base: any;
  makeChart() {
    if (!(this.data?.length > 0)) {
      this.chartOption = null;
      return;
    }

    const min = +Math.min(...this.data.map((item) => item.ndmiAverangeValue)).toFixed(2);
    const max = +Math.max(...this.data.map((item) => item.ndmiAverangeValue)).toFixed(2);
    this.lastValue = this.data[this.data.length - 1];

    const startDate = this.data[0].date.toJSDate();
    const endDate = this.data[this.data.length - 1].date.toJSDate();

    // Generate tick positions (1st day of each month between start and end)
    const ticks: Date[] = [];
    const current = new Date(startDate);
    current.setDate(1);

    while (current <= endDate) {
      ticks.push(new Date(current));
      current.setMonth(current.getMonth() + 1);
    }

    this.chartOption = {
      title: { show: false },
      grid: {
        left: '50px',
        right: '50px',
        top: '10px',
        bottom: '50px',
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          label: {
            backgroundColor: '#f0f0f0',
            borderColor: '#bbb',
            borderWidth: 1,
            color: '#000',
          },
        },
        formatter: (params: any) => {
          const index = params[0].dataIndex;
          const date = this.luxonFormatPipe.transform(
            this.data[index].date,
            'yyyy-MM-dd'
          );
          const minValue = this.data[index].ndmiMinValue.toFixed(2);
          const maxValue = this.data[index].ndmiMaxValue.toFixed(2);
          const avg = this.data[index].ndmiAverangeValue.toFixed(2);

          return `
                    <b>${date}</b><br/>
                    ${this.translateService.instant('doktar_app.common.min')}: ${minValue}<br/>
                    ${this.translateService.instant('doktar_app.common.max')}: ${maxValue}<br/>
                    ${this.translateService.instant('doktar_app.common.average')}: ${avg}
                  `;
        },
      },
      xAxis: {
        type: 'time',
        min: startDate,
        max: endDate,
        axisLabel: {
          formatter: (value: number) => {
            const date = new Date(value);
            const luxonDate = DateTime.fromJSDate(date);
            return this.luxonFormatPipe.transform(
              luxonDate,
              'LLL',
            );
          },
        },
        axisTick: {
          show: true,
        },
        splitLine: { show: false },
        splitNumber: ticks.length,
      },
      yAxis: {
        type: 'value',
        axisLine: { show: false },
        axisLabel: { color: '#444' },
        splitLine: {
          lineStyle: { color: '#e0e0e0', type: 'dashed' },
        },
        splitNumber: 4,
        interval: 0.4,
        min,
        max,
      },
      visualMap: {
        show: false,
        type: 'continuous',
        dimension: 1,
        min,
        max,
        inRange: {
          color: ['#88EBF8', '#20A6DE', '#018BD6', '#0000B3'],
        },
      },
      series: [
        {
          name: this.translateService.instant('doktar_app.common.min'),
          type: 'line',
          data: this.data.map((item) => [item.date.toJSDate(), item.ndmiMinValue]),
          lineStyle: { opacity: 0 },
          stack: 'confidence-band',
          symbol: 'none',
          connectNulls: true,
        },
        {
          name: this.translateService.instant('doktar_app.common.max'),
          type: 'line',
          data: this.data.map((item) => [item.date.toJSDate(), item.ndmiMaxValue]),
          lineStyle: { opacity: 0 },
          areaStyle: { color: '#deeff5' },
          stack: 'confidence-band',
          symbol: 'none',
          connectNulls: true,
        },
        {
          name: this.translateService.instant('doktar_app.common.average'),
          type: 'line',
          data: this.data.map((item) => [item.date.toJSDate(), item.ndmiAverangeValue]),
          lineStyle: {
            width: 3,
          },
          showSymbol: false,
          connectNulls: true,
        },
      ],
    };
  }
}
